import { Dispatch } from 'react';

import PollAPI, { PollAPIPageSize } from '../../api/PollAPI';
import { APIGetPaginatedParams } from '../../api/types';
import { AnonymousUserContextType } from '../../contexts/anonUser';
import { SessionContextType } from '../../contexts/session';
import { VotesInContext } from '../../contexts/types';
import { UserActivity } from '../../contexts/userActivity';
import UserController from '../../controllers/UserController';
import { PollRecord } from '../../models/Poll';
import Logger from '../../utils/logger';

const actionsToAlert = 10;

export const Notifications = {
    Intro:
        'Have fun comparing, sharing, and connecting with friends. ' +
        'Pick a side and see what others choose. ' +
        'Express yourself and create new trends!',
};

const minNewPollsToAddOnReadMorePolls = Math.round(PollAPIPageSize / 2);

export interface HomeControllerState {
    endOfList: boolean;
    lastKey?: {
        [key: string]: any;
    };
}
export class HomeController {
    public static alertDueToUserActivity = (
        userActivity: UserActivity,
    ): boolean => {
        const totalActivity =
            userActivity.comments +
            userActivity.pollsCreated +
            userActivity.shares +
            userActivity.votes;

        return totalActivity > 0 && totalActivity % actionsToAlert === 0;
    };

    public static readMorePolls = async (
        sessionContext: SessionContextType,
        anonymousUserContext: AnonymousUserContextType,
        polls: PollRecord[],
        setPolls: Dispatch<React.SetStateAction<PollRecord[]>>,
        state: HomeControllerState,
        setState: Dispatch<React.SetStateAction<HomeControllerState>>,
    ): Promise<Boolean> => {
        let newPollsAdded = 0;
        let cumulativePolls: PollRecord[] = [];

        const votes = HomeController.getVotes(
            sessionContext,
            anonymousUserContext,
        );

        if (votes === undefined) {
            await HomeController.getUserVotes(
                sessionContext,
                anonymousUserContext,
            );
        } else {
            let newState: HomeControllerState = state;

            while (
                !newState.endOfList &&
                newPollsAdded < minNewPollsToAddOnReadMorePolls
            ) {
                const params: APIGetPaginatedParams = newState.lastKey
                    ? { lastKey: newState.lastKey }
                    : {};

                const result = await PollAPI.getMany(params);

                if (result.data) {
                    if (result.data.length > 0) {
                        const newPolls = HomeController.removeVotedPolls(
                            result.data,
                            votes,
                        );

                        if (newPolls.length > 0) {
                            cumulativePolls = [...cumulativePolls, ...newPolls];
                            setPolls([...polls, ...cumulativePolls]);
                            newPollsAdded += newPolls.length;
                        }
                    }

                    newState = {
                        lastKey: result.lastKey ?? undefined,
                        endOfList: !Boolean(result.lastKey),
                    };
                }

                setState(newState);
            }
        }

        return (
            (Boolean(polls) && Boolean(polls.length)) || Boolean(newPollsAdded)
        );
    };

    private static getUserVotes = async (
        sessionContext: SessionContextType,
        anonymousUserContext: AnonymousUserContextType,
    ): Promise<void> => {
        try {
            if (sessionContext.session.user && !sessionContext.session.votes) {
                await UserController.populateUserVotes(sessionContext);
            } else if (
                anonymousUserContext.anonUser.user &&
                !anonymousUserContext.anonUser.votes
            ) {
                await UserController.populateAnonymousUserVotes(
                    anonymousUserContext,
                );
            }
        } catch (e: unknown) {
            Logger.error(
                'Error in HomeController.getUserVotes(). ' + JSON.stringify(e),
            );
        }
    };

    private static getVotes = (
        sessionContext: SessionContextType,
        anonymousUserContext: AnonymousUserContextType,
    ): VotesInContext | undefined => {
        if (sessionContext.session.user) {
            return sessionContext.session.votes;
        } else if (anonymousUserContext.anonUser.user) {
            return anonymousUserContext.anonUser.votes;
        }

        return undefined;
    };

    private static removeVotedPolls = (
        polls: PollRecord[],
        votes: VotesInContext,
    ): PollRecord[] => {
        const filteredPolls: PollRecord[] = [];

        if (polls.length > 0) {
            polls.forEach((poll) => {
                if (votes && !Boolean(votes[String(poll.id)])) {
                    filteredPolls.push(poll);
                }
            });
        }

        return filteredPolls;
    };
}
